import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AssetMetadata } from '../types';
import styled from 'styled-components';
import { assetService } from '../api/assetService';

const Card = styled.div`
    border: 1px solid #e1e1e1;
    border-radius: 8px;
    padding: 16px;
    margin: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s;
    &:hover {
        transform: translateY(-4px);
    }
`;

const PreviewContainer = styled.div`
    width: 100%;
    height: 200px;
    margin-bottom: 16px;
    border-radius: 4px;
    overflow: hidden;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const PreviewImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
`;

const VideoWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #000;
`;

const PreviewVideo = styled.video`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
`;

const Title = styled.h3`
    margin: 0 0 8px 0;
    font-size: 1.2rem;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    
    &:hover::after {
        content: attr(data-title);
        position: absolute;
        bottom: 100%;
        left: 0;
        background-color: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 0.9rem;
        white-space: normal;
        width: max-content;
        max-width: 300px;
        z-index: 1;
    }
`;

const Description = styled.p`
    margin: 0 0 12px 0;
    color: #666;
    font-size: 0.9rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    cursor: help;
    transition: color 0.2s ease;

    &:hover {
        color: #333;
    }
`;

const Tooltip = styled.div`
    visibility: hidden;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: white;
    color: #333;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: normal;
    width: max-content;
    max-width: 400px;
    min-width: 200px;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    margin-bottom: 12px;
    word-wrap: break-word;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    border: 1px solid #eaeaea;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 6px;
        border-style: solid;
        border-color: white transparent transparent transparent;
        filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1));
    }

    &::before {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 7px;
        border-style: solid;
        border-color: #eaeaea transparent transparent transparent;
    }
`;

const DescriptionWrapper = styled.div`
    position: relative;
    
    &:hover ${Tooltip} {
        visibility: visible;
        opacity: 1;
        transform: translateX(-50%) translateY(-4px);
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

const Tag = styled.span`
    background-color: #e1e1e1;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    color: #666;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FolderTagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    position: relative;
`;

const FolderTag = styled.span`
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    border: 1px solid #bbdefb;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: help;
    transition: all 0.2s ease;

    &:hover {
        background-color: #bbdefb;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
`;

const FolderTagTooltip = styled.div`
    visibility: hidden;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #1976d2;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.8rem;
    white-space: normal;
    width: max-content;
    max-width: 300px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    margin-bottom: 8px;
    word-wrap: break-word;
    opacity: 0;
    transition: all 0.2s ease-in-out;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 5px;
        border-style: solid;
        border-color: #1976d2 transparent transparent transparent;
    }
`;

const FolderTagWrapper = styled.div`
    position: relative;
    
    &:hover ${FolderTagTooltip} {
        visibility: visible;
        opacity: 1;
        transform: translateX(-50%) translateY(-4px);
    }
`;

const FolderTagsSection = styled.div`
    margin-top: 8px;
`;

const FolderTagsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
`;

const FolderTagsLabel = styled.div`
    font-size: 0.75rem;
    color: #666;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const FolderIcon = styled.span`
    font-size: 0.7rem;
    color: #1976d2;
`;

const ShowMoreButton = styled.button`
    background: none;
    border: none;
    color: #1976d2;
    font-size: 0.7rem;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 3px;
    transition: all 0.2s ease;
    text-decoration: underline;

    &:hover {
        background-color: #e3f2fd;
        color: #1565c0;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
    }
`;

const CollapsibleFolderTags = styled.div<{ isExpanded: boolean }>`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: ${(props: { isExpanded: boolean }) => props.isExpanded ? 'none' : '32px'};
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        right: 0;
        width: 20px;
        height: 20px;
        background: linear-gradient(90deg, transparent, white);
        display: ${(props: { isExpanded: boolean }) => props.isExpanded ? 'none' : 'block'};
        pointer-events: none;
    }
`;

const TagsLabel = styled.div`
    font-size: 0.75rem;
    color: #999;
    margin-bottom: 4px;
    font-weight: 500;
`;

const Metadata = styled.div`
    margin-top: 12px;
    font-size: 0.8rem;
    color: #999;
`;

// AI Tags Styled Components
const AITag = styled.span`
    background-color: #f3e5f5;
    color: #7b1fa2;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    border: 1px solid #e1bee7;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: help;
    transition: all 0.2s ease;

    &:hover {
        background-color: #e1bee7;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
`;

const AITagTooltip = styled.div`
    visibility: hidden;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #7b1fa2;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.8rem;
    white-space: normal;
    width: max-content;
    max-width: 300px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    margin-bottom: 8px;
    word-wrap: break-word;
    opacity: 0;
    transition: all 0.2s ease-in-out;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 5px;
        border-style: solid;
        border-color: #7b1fa2 transparent transparent transparent;
    }
`;

const AITagWrapper = styled.div`
    position: relative;
    
    &:hover ${AITagTooltip} {
        visibility: visible;
        opacity: 1;
        transform: translateX(-50%) translateY(-4px);
    }
`;

const AITagsSection = styled.div`
    margin-top: 8px;
`;

const AITagsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
`;

const AITagsLabel = styled.div`
    font-size: 0.75rem;
    color: #666;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const AIIcon = styled.span`
    font-size: 0.7rem;
    color: #7b1fa2;
`;

const CollapsibleAITags = styled.div<{ isExpanded: boolean }>`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: ${(props: { isExpanded: boolean }) => props.isExpanded ? 'none' : '32px'};
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        right: 0;
        width: 20px;
        height: 20px;
        background: linear-gradient(90deg, transparent, white);
        display: ${(props: { isExpanded: boolean }) => props.isExpanded ? 'none' : 'block'};
        pointer-events: none;
    }
`;

// AI Metadata Styled Components
const AIMetadataSection = styled.div`
    margin-top: 16px;
    padding: 12px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #6f42c1;
`;

const AIMetadataHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const AIMetadataTitle = styled.div`
    font-size: 0.9rem;
    font-weight: 600;
    color: #6f42c1;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const AIMetadataIcon = styled.span`
    font-size: 0.8rem;
`;

const AIMetadataGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
`;

const MetadataItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const MetadataLabel = styled.div`
    font-size: 0.75rem;
    font-weight: 500;
    color: #666;
    text-transform: capitalize;
    letter-spacing: 0.5px;
`;

const MetadataValue = styled.div`
    font-size: 0.85rem;
    color: #333;
    line-height: 1.4;
    word-break: break-word;
`;

// Portal-based Tooltip for AI Metadata - escapes grid constraints
const PortalTooltip = styled.div<{ isVisible: boolean; x: number; y: number }>`
    position: fixed;
    left: ${props => props.x}px;
    top: ${props => props.y}px;
    background-color: rgba(0, 0, 0, 0.95);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 0.85rem;
    line-height: 1.5;
    white-space: pre-wrap;
    width: max-content;
    max-width: 500px;
    min-width: 200px;
    z-index: 9999;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    word-wrap: break-word;
    opacity: ${props => props.isVisible ? 1 : 0};
    visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
    transition: all 0.2s ease-in-out;
    pointer-events: none;
    transform: translateX(-50%) translateY(-100%);

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 6px;
        border-style: solid;
        border-color: rgba(0, 0, 0, 0.95) transparent transparent transparent;
        filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3));
    }

    &::before {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 7px;
        border-style: solid;
        border-color: rgba(255, 255, 255, 0.1) transparent transparent transparent;
    }
`;

// Simple CSS-based Tooltip - exactly like the working description tooltip
const MetadataTooltip = styled.div`
    visibility: hidden;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.95);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 0.85rem;
    line-height: 1.5;
    white-space: pre-wrap;
    width: max-content;
    max-width: 500px;
    min-width: 200px;
    z-index: 9999;  // Higher z-index to break out of grid constraints
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 12px;
    word-wrap: break-word;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    pointer-events: none;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 6px;
        border-style: solid;
        border-color: rgba(0, 0, 0, 0.95) transparent transparent transparent;
        filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3));
    }

    &::before {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 7px;
        border-style: solid;
        border-color: rgba(255, 255, 255, 0.1) transparent transparent transparent;
    }
`;

const MetadataValueWithTooltip = styled.div`
    font-size: 0.85rem;
    color: #333;
    line-height: 1.4;
    word-break: break-word;
    cursor: help;
    position: relative;
    max-width: 100%;
`;

const ExpandableSection = styled.div<{ isExpanded: boolean }>`
    max-height: ${(props: { isExpanded: boolean }) => props.isExpanded ? '1000px' : '120px'};
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 20px;
        background: linear-gradient(180deg, transparent, #f8f9fa);
        display: ${(props: { isExpanded: boolean }) => props.isExpanded ? 'none' : 'block'};
        pointer-events: none;
    }
`;

const DebugInfo = styled.div`
    padding: 8px;
    font-size: 0.8rem;
    color: #666;
    background-color: #f0f0f0;
    border-radius: 4px;
    margin-top: 8px;
    
    > div {
        margin-bottom: 4px;
        word-break: break-word;
    }
`;

const ExpandButton = styled.button`
    background: none;
    border: none;
    color: #6f42c1;
    font-size: 0.75rem;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
    text-decoration: underline;
    margin-top: 8px;

    &:hover {
        background-color: #e9ecef;
        color: #5a32a3;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(111, 66, 193, 0.2);
    }
`;

interface AssetCardProps {
    asset: AssetMetadata;
    onDelete?: (assetId: string) => void;
}

const DeleteButton = styled.button`
position: absolute;
top: 8px;
right: 8px;
background: rgba(255, 255, 255, 0.9);
border: 1px solid #ff4d4f;
color: #ff4d4f;
border - radius: 4px;
padding: 4px 8px;
font - size: 0.8rem;
cursor: pointer;
opacity: 0;
transition: all 0.2s;
z - index: 10;
    
    ${ Card }: hover & {
    opacity: 1;
}

    &:hover {
    background: #ff4d4f;
    color: white;
}
    
    &:disabled {
    opacity: 0.5;
    cursor: not - allowed;
}
`;

const AssetCard: React.FC<AssetCardProps> = ({ asset, onDelete }: AssetCardProps) => {
    const [isFolderTagsExpanded, setIsFolderTagsExpanded] = useState(false);
    const [isAITagsExpanded, setIsAITagsExpanded] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAIMetadataExpanded, setIsAIMetadataExpanded] = useState(false);

    // Tooltip state for AI metadata
    const [tooltipState, setTooltipState] = useState<{
        isVisible: boolean;
        content: string;
        x: number;
        y: number;
    }>({
        isVisible: false,
        content: '',
        x: 0,
        y: 0
    });

    // Debug mode toggle - set to false for production
    const DEBUG_MODE = false; // Change to true to show debug info
    const CONSOLE_DEBUG = false; // Change to true to show console logs

    // Helper function to safely get metadata length
    const getMetadataLength = (metadata: any): string => {
        try {
            if (!metadata) return 'N/A';
            if (typeof metadata === 'string') return metadata.length.toString();
            if (typeof metadata === 'object' && metadata !== null) {
                const keys = Object.keys(metadata);
                return keys.length.toString();
            }
            return 'Unknown';
        } catch (error) {
            return 'Error';
        }
    };

    // Helper function to detect metadata structure
    const detectMetadataStructure = (metadata: any): string => {
        try {
            if (!metadata) return 'none';
            if (typeof metadata === 'string') return 'string';
            if (typeof metadata === 'object' && metadata !== null) {
                if (metadata.analysis && metadata.analysis.metadata) return 'analysis.metadata';
                if (metadata.metadata) return 'nested metadata';
                if (metadata.analysis) return 'analysis object';
                if (metadata.genre || metadata.style || metadata.quality_score || metadata.category) return 'root level AI fields';
                return 'root level';
            }
            return 'unknown';
        } catch (error) {
            return 'error';
        }
    };

    // Helper function to extract actual values from complex field structures
    const extractFieldValue = (field: any): any => {
        if (!field) return null;

        // Handle object with type/value structure like {"type": "string", "value": "actual value"}
        if (typeof field === 'object' && field.type && field.value !== undefined) {
            return field.value;
        }

        // Handle array fields
        if (Array.isArray(field)) {
            if (field.length === 0) return null;
            return field;
        }

        // Handle primitive values
        if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') {
            return field;
        }

        return field;
    };

    // Helper function to check if a field has meaningful content
    const hasMeaningfulContent = (field: any): boolean => {
        if (!field) return false;

        const value = extractFieldValue(field);
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;

        return true;
    };

    // Parse AI metadata safely - handles multiple nested structures and common metadata patterns
    const parseAIMetadata = (metadata: Record<string, any> | string | null): any => {
        if (CONSOLE_DEBUG) console.log('parseAIMetadata called with:', metadata);

        if (!metadata) {
            if (CONSOLE_DEBUG) console.log('No metadata provided');
            return null;
        }

        try {
            let parsedMetadata: any = null;

            // If metadata is already an object, use it directly
            if (typeof metadata === 'object' && metadata !== null) {
                if (CONSOLE_DEBUG) console.log('Metadata is object:', metadata);
                parsedMetadata = metadata;
            }
            // If metadata is a string, try to parse it
            else if (typeof metadata === 'string') {
                if (CONSOLE_DEBUG) console.log('Metadata is string:', metadata);
                if (metadata.trim() === '') {
                    if (CONSOLE_DEBUG) console.log('Metadata string is empty');
                    return null;
                }

                try {
                    parsedMetadata = JSON.parse(metadata);
                    if (CONSOLE_DEBUG) console.log('Parsed metadata successfully:', parsedMetadata);
                } catch (parseError) {
                    if (CONSOLE_DEBUG) console.error('Failed to parse metadata string:', parseError);
                    if (CONSOLE_DEBUG) console.error('Raw metadata string:', metadata);
                    return null;
                }
            } else {
                if (CONSOLE_DEBUG) console.log('Metadata type not supported:', typeof metadata);
                return null;
            }

            // Define AI-specific fields to look for
            const aiSpecificFields = [
                'genre', 'style', 'technical_quality', 'artistic_style', 'composition', 'mood',
                'gameplay_elements', 'platform_compatibility', 'video_specific', 'quality_score',
                'quality_score1', 'category', 'technical_notes', 'ai_processing_failed',
                'ai_enrichment_available', 'test', 'subjects', 'technical', 'gameplay'
            ];

            // Strategy 1: Check for AI-specific fields at the root level
            const rootLevelAI: any = {};
            aiSpecificFields.forEach(field => {
                if (hasMeaningfulContent(parsedMetadata[field])) {
                    rootLevelAI[field] = extractFieldValue(parsedMetadata[field]);
                }
            });

            if (Object.keys(rootLevelAI).length > 0) {
                if (CONSOLE_DEBUG) console.log('Found AI-specific fields at root level:', Object.keys(rootLevelAI));
                const filteredRootAI = { ...rootLevelAI };
                delete filteredRootAI.tags;
                delete filteredRootAI.description;
                if (CONSOLE_DEBUG) console.log('Returning root level AI fields:', Object.keys(filteredRootAI));
                return filteredRootAI;
            }

            // Strategy 2: Check for nested metadata in analysis.metadata (common pattern)
            if (parsedMetadata.analysis && parsedMetadata.analysis.metadata && typeof parsedMetadata.analysis.metadata === 'object') {
                if (CONSOLE_DEBUG) console.log('Found nested metadata in analysis.metadata:', Object.keys(parsedMetadata.analysis.metadata));
                const filteredAnalysisMetadata: any = {};

                // Extract meaningful fields from analysis.metadata
                Object.keys(parsedMetadata.analysis.metadata).forEach(key => {
                    if (hasMeaningfulContent(parsedMetadata.analysis.metadata[key])) {
                        filteredAnalysisMetadata[key] = extractFieldValue(parsedMetadata.analysis.metadata[key]);
                        if (CONSOLE_DEBUG) console.log(`Extracted field ${ key }: `, filteredAnalysisMetadata[key]);
                    }
                });

                if (Object.keys(filteredAnalysisMetadata).length > 0) {
                    if (CONSOLE_DEBUG) console.log('Returning analysis.metadata with fields:', Object.keys(filteredAnalysisMetadata));
                    return filteredAnalysisMetadata;
                }
            }

            // Strategy 3: Check for nested metadata in the "metadata" field
            if (parsedMetadata.metadata && typeof parsedMetadata.metadata === 'object') {
                if (CONSOLE_DEBUG) console.log('Found nested metadata field:', Object.keys(parsedMetadata.metadata));
                const filteredNestedMetadata = { ...parsedMetadata.metadata };
                delete filteredNestedMetadata.tags;
                delete filteredNestedMetadata.description;
                return filteredNestedMetadata;
            }

            // Strategy 4: Check for AI-related fields in analysis object
            if (parsedMetadata.analysis && typeof parsedMetadata.analysis === 'object') {
                if (CONSOLE_DEBUG) console.log('Found analysis object:', Object.keys(parsedMetadata.analysis));
                const analysisAI: any = {};

                // Extract AI-related fields from analysis
                if (parsedMetadata.analysis.description) analysisAI.description = parsedMetadata.analysis.description;
                if (parsedMetadata.analysis.tags && Array.isArray(parsedMetadata.analysis.tags)) {
                    analysisAI.tags = parsedMetadata.analysis.tags;
                }

                // Check if analysis has metadata
                if (parsedMetadata.analysis.metadata && typeof parsedMetadata.analysis.metadata === 'object') {
                    Object.assign(analysisAI, parsedMetadata.analysis.metadata);
                }

                if (Object.keys(analysisAI).length > 0) {
                    if (CONSOLE_DEBUG) console.log('Found AI data in analysis object:', Object.keys(analysisAI));
                    return analysisAI;
                }
            }

            // Strategy 5: Look for any fields that might contain AI-generated content
            const potentialAIFields = ['ai_', 'enrichment', 'analysis', 'processed', 'generated'];
            const potentialAI: any = {};

            Object.keys(parsedMetadata).forEach(key => {
                if (potentialAIFields.some(aiField => key.toLowerCase().includes(aiField))) {
                    if (parsedMetadata[key] && parsedMetadata[key] !== '' && parsedMetadata[key] !== null && parsedMetadata[key] !== undefined) {
                        potentialAI[key] = parsedMetadata[key];
                    }
                }
            });

            if (Object.keys(potentialAI).length > 0) {
                if (CONSOLE_DEBUG) console.log('Found potential AI fields:', Object.keys(potentialAI));
                return potentialAI;
            }

            if (CONSOLE_DEBUG) console.log('No AI metadata found in any expected location');
            return null;

        } catch (error) {
            if (CONSOLE_DEBUG) console.error('Failed to parse AI metadata:', error);
            if (CONSOLE_DEBUG) console.error('Metadata that failed to parse:', metadata);
            return null;
        }
    };

    const aiMetadata = parseAIMetadata(asset.metadata);
    const hasAIMetadata = aiMetadata && Object.keys(aiMetadata).length > 0;

    // Debug logging
    if (CONSOLE_DEBUG) {
        console.log('Asset metadata field:', asset.metadata);
        console.log('Parsed AI metadata:', aiMetadata);
        console.log('Has AI metadata:', hasAIMetadata);
        console.log('Asset keys:', Object.keys(asset));

        // Check what AI-specific fields are available
        if (aiMetadata) {
            const aiSpecificFields = [
                'genre', 'style', 'technical_quality', 'artistic_style', 'composition', 'mood',
                'gameplay_elements', 'platform_compatibility', 'video_specific', 'quality_score',
                'quality_score1', 'category', 'technical_notes', 'ai_processing_failed',
                'ai_enrichment_available', 'test', 'subjects', 'technical', 'gameplay'
            ];
            const availableFields = aiSpecificFields.filter(field => aiMetadata[field]);
            console.log('Available AI-specific fields:', availableFields);
            console.log('AI metadata keys:', Object.keys(aiMetadata));
            console.log('Sample AI-specific field values:', {
                genre: aiMetadata.genre,
                style: aiMetadata.style,
                technical_quality: aiMetadata.technical_quality,
                quality_score: aiMetadata.quality_score,
                quality_score1: aiMetadata.quality_score1,
                category: aiMetadata.category,
                test: aiMetadata.test
            });
        }

        // Log the parsing strategy that was used
        console.log('Metadata structure detected:', detectMetadataStructure(asset.metadata));
    }

    // Helper function to truncate long text
    const truncateText = (text: string, maxLength: number = 50): string => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Tooltip functions for AI metadata
    const showTooltip = (content: string, element: HTMLElement) => {
        const rect = element.getBoundingClientRect();
        const x = rect.left + rect.width / 2; // Center horizontally
        const y = rect.top - 10; // Above the element

        setTooltipState({
            isVisible: true,
            content,
            x,
            y
        });
    };

    const hideTooltip = () => {
        setTooltipState(prev => ({ ...prev, isVisible: false }));
    };

    // Helper function to format metadata value
    const formatMetadataValue = (value: any, label: string): React.ReactNode => {
        if (value === null || value === undefined || value === '') {
            return React.createElement('span', {
                style: { color: '#999', fontStyle: 'italic' }
            }, 'Not specified');
        }

        // Handle object with type/value structure like {"type": "string", "value": "actual value"}
        if (typeof value === 'object' && value.type && value.value !== undefined) {
            const actualValue = value.value;
            if (actualValue === null || actualValue === undefined || actualValue === '') {
                return React.createElement('span', {
                    style: { color: '#999', fontStyle: 'italic' }
                }, 'Not specified');
            }

            if (typeof actualValue === 'string' && actualValue.length > 50) {
                return (
                    <MetadataValueWithTooltip
                        onMouseEnter={(e) => showTooltip(actualValue, e.currentTarget)}
                        onMouseLeave={hideTooltip}
                    >
                        {truncateText(actualValue, 50)}
                    </MetadataValueWithTooltip>
                );
            }

            return <MetadataValue>{String(actualValue)}</MetadataValue>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return React.createElement('span', {
                    style: { color: '#999', fontStyle: 'italic' }
                }, 'None');
            }
            return value.join(', ');
        }

        if (typeof value === 'object') {
            const jsonString = JSON.stringify(value, null, 2);
            if (typeof jsonString === 'string' && jsonString.length > 50) {
                return (
                    <MetadataValueWithTooltip
                        onMouseEnter={(e) => showTooltip(jsonString, e.currentTarget)}
                        onMouseLeave={hideTooltip}
                    >
                        {truncateText(jsonString, 30)}
                    </MetadataValueWithTooltip>
                );
            }
            return <MetadataValue>{jsonString}</MetadataValue>;
        }

        const stringValue = String(value);
        if (stringValue.length > 50) {
            return (
                <MetadataValueWithTooltip
                    onMouseEnter={(e) => showTooltip(stringValue, e.currentTarget)}
                    onMouseLeave={hideTooltip}
                >
                    {truncateText(stringValue, 50)}
                </MetadataValueWithTooltip>
            );
        }

        return <MetadataValue>{stringValue}</MetadataValue>;
    };

    const getFileExtension = (url: string) => {
        const urlWithoutQuery = url.split('?')[0];
        const lastPart = urlWithoutQuery.split('/').pop() || '';
        const extension = lastPart.split('.').pop()?.toLowerCase();
        return extension || null;
    };

    const fileExtension = asset.sas_url ? getFileExtension(asset.sas_url) : null;
    const isImage = fileExtension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
    const isVideo = fileExtension && ['mp4', 'webm', 'ogg'].includes(fileExtension);

    const hasFolderTags = asset.folder_tags && asset.folder_tags.length > 0;
    const shouldShowMoreFolderButton = hasFolderTags && asset.folder_tags!.length > 3;

    const hasAITags = asset.tags && asset.tags.length > 0;
    const shouldShowMoreAIButton = hasAITags && asset.tags!.length > 3;

    const toggleFolderTags = () => {
        setIsFolderTagsExpanded(!isFolderTagsExpanded);
    };

    const toggleAITags = () => {
        setIsAITagsExpanded(!isAITagsExpanded);
    };

    const handleDelete = async () => {
        if (!onDelete) return;

        if (window.confirm('Are you sure you want to delete this asset?')) {
            setIsDeleting(true);
            try {
                await assetService.deleteAsset(asset.id);
                onDelete(asset.id);
            } catch (error) {
                console.error('Failed to delete asset:', error);
                // We could add toast notification here, but console error is fine for now
            } finally {
                // If it was successful, the component will unmount so state update isn't strictly necessary, 
                // but good practice in case it failed
                setIsDeleting(false);
            }
        }
    };

    return (
        <Card>
            {onDelete && (
                <DeleteButton
                    onClick={handleDelete}
                    disabled={isDeleting}
                    title="Delete asset"
                >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </DeleteButton>
            )}

            {/* Portal Tooltip for AI Metadata */}
            {createPortal(
                <PortalTooltip
                    isVisible={tooltipState.isVisible}
                    x={tooltipState.x}
                    y={tooltipState.y}
                >
                    {tooltipState.content}
                </PortalTooltip>,
                document.body
            )}

            <PreviewContainer>
                {isImage && asset.sas_url && (
                    <PreviewImage
                        src={asset.sas_url}
                        alt={asset.title}
                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                            console.error('Error loading image:', e);
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                        }}
                    />
                )}
                {isVideo && asset.sas_url && (
                    <VideoWrapper>
                        <PreviewVideo
                            controls
                            playsInline
                            preload="metadata"
                            onError={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
                                console.error('Error loading video:', e);
                                const target = e.target as HTMLVideoElement;
                                target.style.display = 'none';
                            }}
                        >
                            <source src={asset.sas_url} type={`video / ${ fileExtension } `} />
                            Your browser does not support the video tag.
                        </PreviewVideo>
                    </VideoWrapper>
                )}
                {!isImage && !isVideo && (
                    React.createElement('div', {
                        style: { color: '#999', textAlign: 'center' }
                    }, 'Preview not available')
                )}
            </PreviewContainer>
            <Title data-title={asset.title}>{asset.title}</Title>
            <DescriptionWrapper>
                <Description>{asset.description || ''}</Description>
                <Tooltip>{asset.description || ''}</Tooltip>
            </DescriptionWrapper>

            {/* Debug: Show basic asset info - COMMENTED OUT FOR PRODUCTION */}
            {DEBUG_MODE && (
                <div style={{ padding: '8px', margin: '8px 0', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <div><strong>Debug Info:</strong></div>
                    <div>Asset ID: {asset.id}</div>
                    <div>Asset Type: {asset.asset_type}</div>
                    <div>Project ID: {asset.project_id}</div>
                    <div>Has Metadata: {asset.metadata ? 'Yes' : 'No'}</div>
                    <div>Metadata Type: {typeof asset.metadata}</div>
                    <div>Metadata Length: {getMetadataLength(asset.metadata)}</div>
                    <div>Metadata Keys: {asset.metadata && typeof asset.metadata === 'object' ? Object.keys(asset.metadata).join(', ') : 'N/A'}</div>
                    <div>Tags Count: {asset.tags ? asset.tags.length : 0}</div>
                    <div>Folder Tags Count: {asset.folder_tags ? asset.folder_tags.length : 0}</div>
                    <div>AI Metadata Available: {hasAIMetadata ? 'Yes' : 'No'}</div>
                    {aiMetadata && (
                        <>
                            <div>AI Metadata Fields: {Object.keys(aiMetadata).join(', ')}</div>
                            <div>Metadata Type: {Object.keys(aiMetadata).some(key =>
                                ['genre', 'style', 'technical_quality', 'artistic_style', 'composition', 'mood', 'gameplay_elements', 'platform_compatibility', 'video_specific'].includes(key)
                            ) ? 'Root-level AI fields' : 'Nested metadata'}</div>
                            <div>Sample Values:</div>
                            <div style={{ marginLeft: '10px' }}>
                                {Object.entries(aiMetadata).slice(0, 5).map(([key, value]) => (
                                    <div key={key}>{key}: {typeof value === 'string' ? value.substring(0, 50) + (value.length > 50 ? '...' : '') : JSON.stringify(value).substring(0, 50) + (JSON.stringify(value).length > 50 ? '...' : '')}</div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* AI Metadata Section */}
            {hasAIMetadata ? (
                <AIMetadataSection>
                    <AIMetadataHeader>
                        <AIMetadataTitle>
                            <AIMetadataIcon>🧠</AIMetadataIcon>
                            AI Analysis & Metadata
                        </AIMetadataTitle>
                        <ExpandButton
                            type="button"
                            onClick={() => {
                                if (CONSOLE_DEBUG) console.log('Toggle AI metadata, current state:', isAIMetadataExpanded);
                                setIsAIMetadataExpanded(!isAIMetadataExpanded);
                            }}
                        >
                            {isAIMetadataExpanded ? 'Show less' : 'Show more'}
                        </ExpandButton>
                    </AIMetadataHeader>

                    <ExpandableSection isExpanded={isAIMetadataExpanded}>
                        <AIMetadataGrid>
                            {/* Dynamically render all metadata fields */}
                            {Object.entries(aiMetadata).map(([key, value]) => (
                                <MetadataItem key={key}>
                                    <MetadataLabel>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</MetadataLabel>
                                    {formatMetadataValue(value, key)}
                                </MetadataItem>
                            ))}
                        </AIMetadataGrid>
                    </ExpandableSection>
                </AIMetadataSection>
            ) : asset.metadata ? (
                // Show raw metadata if parsing failed
                <AIMetadataSection>
                    <AIMetadataHeader>
                        <AIMetadataTitle>
                            <AIMetadataIcon>⚠️</AIMetadataIcon>
                            Raw Metadata (AI Parsing Failed)
                        </AIMetadataTitle>
                        <ExpandButton
                            type="button"
                            onClick={() => setIsAIMetadataExpanded(!isAIMetadataExpanded)}
                        >
                            {isAIMetadataExpanded ? 'Show less' : 'Show more'}
                        </ExpandButton>
                    </AIMetadataHeader>

                    <ExpandableSection isExpanded={isAIMetadataExpanded}>
                        <DebugInfo>
                            <div><strong>Raw metadata (could not extract AI-specific fields):</strong></div>
                            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '8px' }}>
                                Note: This shows the raw metadata that couldn't be parsed for AI-specific fields. The parser tried multiple strategies:
                            </div>
                            <ul style={{ fontSize: '0.75rem', color: '#666', marginBottom: '8px', marginLeft: '20px' }}>
                                <li>Root-level AI fields (genre, style, quality_score, etc.)</li>
                                <li>Nested analysis.metadata structure</li>
                                <li>Nested metadata field</li>
                                <li>Analysis object with AI content</li>
                                <li>Potential AI-related field names</li>
                            </ul>
                            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '8px' }}>
                                <strong>Detected structure:</strong> {detectMetadataStructure(asset.metadata)}
                            </div>
                            <pre style={{ fontSize: '0.7rem', overflow: 'auto', maxHeight: '200px' }}>
                                {typeof asset.metadata === 'string' ? asset.metadata : JSON.stringify(asset.metadata, null, 2)}
                            </pre>
                        </DebugInfo>
                    </ExpandableSection>
                </AIMetadataSection>
            ) : (
                // Debug section to show what's actually in the asset
                <AIMetadataSection>
                    <AIMetadataHeader>
                        <AIMetadataTitle>
                            <AIMetadataIcon>🔍</AIMetadataIcon>
                            Debug Info
                        </AIMetadataTitle>
                    </AIMetadataHeader>
                    <DebugInfo>
                        <div>Asset metadata field type: {typeof asset.metadata}</div>
                        <div>Asset metadata value: {JSON.stringify(asset.metadata, null, 2)}</div>
                        <div>Available asset keys: {Object.keys(asset).join(', ')}</div>
                    </DebugInfo>
                </AIMetadataSection>
            )}

            {hasAITags && (
                <AITagsSection>
                    <AITagsHeader>
                        <AITagsLabel>
                            <AIIcon>🤖</AIIcon>
                            AI Tags ({asset.tags!.length})
                        </AITagsLabel>
                        {shouldShowMoreAIButton && (
                            <ShowMoreButton onClick={toggleAITags}>
                                {isAITagsExpanded ? 'Show less' : 'Show more'}
                            </ShowMoreButton>
                        )}
                    </AITagsHeader>
                    <CollapsibleAITags isExpanded={isAITagsExpanded || !shouldShowMoreAIButton}>
                        {asset.tags!.map((tag: string, index: number) => (
                            <AITagWrapper key={index}>
                                <AITag title={tag}>{tag}</AITag>
                                <AITagTooltip>{tag}</AITagTooltip>
                            </AITagWrapper>
                        ))}
                    </CollapsibleAITags>
                </AITagsSection>
            )}
            {hasFolderTags && (
                <FolderTagsSection>
                    <FolderTagsHeader>
                        <FolderTagsLabel>
                            <FolderIcon>📁</FolderIcon>
                            Folders ({asset.folder_tags!.length})
                        </FolderTagsLabel>
                        {shouldShowMoreFolderButton && (
                            <ShowMoreButton onClick={toggleFolderTags}>
                                {isFolderTagsExpanded ? 'Show less' : 'Show more'}
                            </ShowMoreButton>
                        )}
                    </FolderTagsHeader>
                    <CollapsibleFolderTags isExpanded={isFolderTagsExpanded || !shouldShowMoreFolderButton}>
                        {asset.folder_tags!.map((tag: string, index: number) => (
                            <FolderTagWrapper key={index}>
                                <FolderTag title={tag}>{tag}</FolderTag>
                                <FolderTagTooltip>{tag}</FolderTagTooltip>
                            </FolderTagWrapper>
                        ))}
                    </CollapsibleFolderTags>
                </FolderTagsSection>
            )}
            <Metadata>
                {React.createElement('div', {}, `Type: ${ asset.asset_type } `)}
                {React.createElement('div', {}, `Indexed: ${ new Date(asset.indexed_at).toLocaleDateString() } `)}
            </Metadata>
        </Card>
    );
};

export default AssetCard;